import 'keen-slider/keen-slider.min.css';

import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import CTA from 'react-scale/components/ui/cta';
import Logo from 'react-scale/components/website/Logo';
import { useState } from 'react';
import Video from 'react-scale/components/ui/video';
import AspectRatioBox from 'react-scale/components/pages/transformx/common/aspect-ratio-box';

const HeroSlider = ({ icon, title, subtitle, assets, cta }) => {
  const [sliderRef] = useKeenSlider({
    slides: {
      centered: true,
      perView: 1.015,
      spacing: 24
    }
  });

  return (
    <div className="relative">
      <div className="container py-18 md:py-28 xl:pt-32 xl:pb-36">
        <div className="max-w-full relative inline-block md:flex items-center md:space-x-16 xl:space-x-32">
          <div className="space-y-12 md:space-y-4 xl:space-y-10 xl:min-w-max">
            <div className="flex flex-col md:flex-row items-center md:space-x-6 lg:space-x-8 space-y-6 md:space-y-0">
              {icon.src && (
                <Image
                  src={icon.src}
                  layout="fixed"
                  height={icon.height}
                  width={icon.width}
                  quality={100}
                  alt={icon.alt}
                />
              )}
              <div className="flex-1 flex flex-col items-center md:items-start">
                <picture className="hidden sm:block">
                  <Logo width="82px" fill="#A3A3A3" fillmobile="#A3A3A3" />
                </picture>
                <picture className="sm:hidden">
                  <Logo width="67px" fillmobile="#A3A3A3" />
                </picture>
                <h1 className="font-aeonik mt-4 text-4xl md:text-5xl md:leading-normal xl:text-6xl xl:leading-[67px]">
                  {title}
                </h1>
              </div>
            </div>
            <h2 className="text-lg text-center md:text-left leading-8 md:text-xl md:leading-9 lg:max-w-sm xl:text-2xl lg:font-aeonik xl:leading-10 xl:mt-8">
              {subtitle}
            </h2>
            <div className="w-screen left-1/2 -translate-x-1/2 relative px-6 overflow-hidden md:hidden space-y-6">
              <div className="keen-slider !overflow-visible" ref={sliderRef}>
                {assets.map((slide, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className="keen-slider__slide">
                    <AspectRatioBox width={696} height={392} className="relative" responsive>
                      {slide.asVideo ? (
                        <Video url={slide.url} poster={slide.poster} smallButton blackButton />
                      ) : (
                        <Image
                          src={slide.src}
                          alt={slide.alt}
                          layout="fill"
                          quality={100}
                          objectFit="cover"
                          className="rounded-2xl"
                        />
                      )}
                    </AspectRatioBox>
                  </div>
                ))}
              </div>
            </div>
            <div className="mx-auto md:mx-0 max-w-max">
              <CTA cta={cta} />
            </div>
          </div>
          <div className="min-w-0 md:w-1/2 lg:w-8/12 xl:w-full hidden lg:block">
            <Slider slides={assets} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Slider = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [opacities, setOpacities] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, slider] = useKeenSlider({
    created() {
      setLoaded(true);
    },
    slides: slides.length,
    loop: true,
    detailsChanged(s) {
      const newOpacities = s.track.details.slides.map(slide => slide.portion);
      setOpacities(newOpacities);
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    }
  });

  return (
    <>
      <div ref={sliderRef} className="relative fader lg:h-[306px] xl:h-[378px]">
        {slides.map((slide, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div
            style={{
              opacity: opacities[index],
              filter: 'drop-shadow(0 25px 50px rgba(74, 74, 139, 0.25))'
            }}
            className="fader__slide absolute top-0 w-full h-full"
          >
            {slide.asVideo ? (
              <Video url={slide.url} poster={slide.poster} smallButton blackButton />
            ) : (
              <>
                <AspectRatioBox width={696} height={392} className="relative" responsive>
                  <Image
                    src={slide.src}
                    alt={slide.alt}
                    layout="fill"
                    quality={100}
                    objectFit="cover"
                    className="rounded-2xl"
                  />
                </AspectRatioBox>
              </>
            )}
          </div>
        ))}
      </div>

      {loaded && slides && slider && (
        <div className="relative">
          <div className="flex max-w-max mx-auto text-lg mt-6">
            <button
              type="button"
              className="absolute left-1/3"
              onClick={e => e.stopPropagation() || slider.current.prev()}
            >
              ←
            </button>
            <p>{slides[currentSlide].epigraph}</p>
            <button
              type="button"
              className="absolute left-2/3"
              onClick={e => e.stopPropagation() || slider.current.next()}
            >
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSlider;
